// Loader for the user's custom building models (OBJ + colors, no textures — modeled on the laptop "NEOWULF"
// and Syncthing-mirrored into C:\Users\User\Desktop\projects\Uma3, then copied in here per model).
//
// Parsed SYNCHRONOUSLY at module load (text bundled via ?raw) so the factory's synchronous makeMesh() can use
// it with no load race. Per model we designate the collider group(s); some models OVERHANG their tile (e.g. the
// dropper's drop-arm), so they anchor their re-origin on the TILE group, not the whole-geometry center. Any
// TRANSPARENT part (mtl `d`<1) is treated as a DROP POINT — where ore spawns, with no collision (matches how the
// user authored them: the ore is the size of that glass cube and spawns inside it).

import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import conveyorObj from "./conveyor.obj?raw";
import conveyorMtl from "./conveyor.mtl?raw";
import dropperSingleObj from "./droppersingle.obj?raw";
import dropperSingleMtl from "./droppersingle.mtl?raw";
import dropperDoubleObj from "./dropperdouble.obj?raw";
import dropperDoubleMtl from "./dropperdouble.mtl?raw";
import rampObj from "./conveyorramp.obj?raw";
import rampMtl from "./conveyorramp.mtl?raw";
import splitterObj from "./splitter.obj?raw";
import splitterMtl from "./splitter.mtl?raw";
import wallObj from "./conveyorwall.obj?raw";
import wallMtl from "./conveyorwall.mtl?raw";
import blasterObj from "./blaster.obj?raw";
import blasterMtl from "./blaster.mtl?raw";
import freezerObj from "./freezer.obj?raw";
import freezerMtl from "./freezer.mtl?raw";
import pressurizerObj from "./pressurizer.obj?raw";
import pressurizerMtl from "./pressurizer.mtl?raw";
import magnetronObj from "./magnetron.obj?raw";
import magnetronMtl from "./magnetron.mtl?raw";
import infernaceObj from "./infernace.obj?raw";
import infernaceMtl from "./infernace.mtl?raw";
import leechObj from "./leech.obj?raw";
import leechMtl from "./leech.mtl?raw";
import ecoboxObj from "./ecobox.obj?raw";
import ecoboxMtl from "./ecobox.mtl?raw";
import scannerObj from "./scanner.obj?raw";
import scannerMtl from "./scanner.mtl?raw";
import obliteratorObj from "./obliterator.obj?raw";
import obliteratorMtl from "./obliterator.mtl?raw";
import orecannonObj from "./orecannon.obj?raw";
import orecannonMtl from "./orecannon.mtl?raw";
import receptacleObj from "./receptacle.obj?raw";
import receptacleMtl from "./receptacle.mtl?raw";
import enricherObj from "./enricher.obj?raw";
import enricherMtl from "./enricher.mtl?raw";
import teleporteraObj from "./teleportera.obj?raw";
import teleporteraMtl from "./teleportera.mtl?raw";
import teleporterbObj from "./teleporterb.obj?raw";
import teleporterbMtl from "./teleporterb.mtl?raw";
import triplexerObj from "./triplexer.obj?raw";
import triplexerMtl from "./triplexer.mtl?raw";
import schrodingerObj from "./schrodinger.obj?raw";
import schrodingerMtl from "./schrodinger.mtl?raw";
import diversifierObj from "./diversifier.obj?raw";
import diversifierMtl from "./diversifier.mtl?raw";
import mutatorObj from "./mutator.obj?raw";
import mutatorMtl from "./mutator.mtl?raw";
import laserObj from "./laser.obj?raw";
import laserMtl from "./laser.mtl?raw";
import packerObj from "./packer.obj?raw";
import packerMtl from "./packer.mtl?raw";
import vatObj from "./vat.obj?raw";
import vatMtl from "./vat.mtl?raw";

/** One separable part of a building model (an OBJ `g`-group) with its flat color(s) + opacity(ies). */
export interface ModelPart {
  name: string;
  geometry: THREE.BufferGeometry; // re-origined + yaw-corrected. Carries material `groups` if multi-material.
  colors: number[];               // sRGB hex per material (materialIndex order); length 1 for single-material.
  opacities: number[];            // per material; 1 = opaque, <1 = transparent (glass).
}
export interface FactoryModel {
  parts: ModelPart[];
  /** AABB collider for the physics ore system — only the designated collider group(s). */
  collider: { size: [number, number, number]; center: [number, number, number] } | null;
  /** Ore SPAWN points (centers of transparent parts), in local space post origin/yaw. No collision. */
  dropPoints: [number, number, number][];
}

interface BuildOpts {
  colliderGroups: string[];
  originGroup?: string; // center XZ + drop base-Y on THIS group's AABB (for overhanging models); default = whole model
  yaw?: number;         // model-local yaw correction (radians), for an asset authored off the engine's +Z-forward
  recover?: (parts: ModelPart[]) => void; // one-off geometry surgery BEFORE re-origin (e.g. fix a mis-sized ramp)
  shift?: [number, number, number];       // extra local translate AFTER re-origin + yaw (e.g. anchor a ramp on its foot)
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Pull flat diffuse color (Kd) + opacity (d) per material; texture maps (map_*) are ignored on purpose.
 *  Kd is LINEAR (Blender's OBJ exporter writes the linear base color) → convert to an sRGB hex so the material
 *  displays the color as authored (treating it as sRGB directly, as we used to, dulled every model's colors). */
function parseMtl(mtl: string): Record<string, { color: number; opacity: number }> {
  const out: Record<string, { color: number; opacity: number }> = {};
  let cur = "";
  for (const rawLine of mtl.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.startsWith("newmtl ")) { cur = line.slice(7).trim(); out[cur] = { color: 0x808080, opacity: 1 }; }
    else if (cur && line.startsWith("Kd ")) {
      const p = line.split(/\s+/);
      const r = clamp01(parseFloat(p[1])), g = clamp01(parseFloat(p[2])), b = clamp01(parseFloat(p[3]));
      out[cur].color = new THREE.Color().setRGB(r, g, b, THREE.LinearSRGBColorSpace).getHex(); // linear Kd → sRGB hex
    } else if (cur && line.startsWith("d ")) {
      out[cur].opacity = clamp01(parseFloat(line.split(/\s+/)[1]));
    }
  }
  return out;
}

function buildModel(objText: string, mtlText: string, opts: BuildOpts): FactoryModel {
  const matByName = parseMtl(mtlText);
  const root = new OBJLoader().parse(objText); // synchronous; one child Mesh per `g`-group
  const parts: ModelPart[] = [];
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const geometry = (o.geometry as THREE.BufferGeometry).clone();
    if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();
    const mats = Array.isArray(o.material) ? o.material : [o.material as THREE.Material];
    const colors = mats.map((m) => matByName[m.name]?.color ?? 0x808080);
    const opacities = mats.map((m) => matByName[m.name]?.opacity ?? 1);
    parts.push({ name: o.name, geometry, colors, opacities });
  });
  if (opts.recover) opts.recover(parts); // fix/retarget geometry before we re-origin around it
  // Re-origin: center XZ + drop base to y=0 on the anchor (the TILE group for overhangs; else the whole model).
  const anchor = new THREE.Box3();
  const anchorParts = opts.originGroup ? parts.filter((p) => p.name === opts.originGroup) : parts;
  for (const p of anchorParts) { p.geometry.computeBoundingBox(); anchor.union(p.geometry.boundingBox!); }
  const cx = (anchor.min.x + anchor.max.x) / 2, cz = (anchor.min.z + anchor.max.z) / 2, minY = anchor.min.y;
  for (const p of parts) p.geometry.translate(-cx, -minY, -cz);
  // Model-local yaw correction (author-vs-engine forward mismatch); rotates around the now-centered origin.
  if (opts.yaw) for (const p of parts) p.geometry.rotateY(opts.yaw);
  if (opts.shift) for (const p of parts) p.geometry.translate(opts.shift[0], opts.shift[1], opts.shift[2]);
  // Collider = AABB of the designated collider group(s).
  let collider: FactoryModel["collider"] = null;
  const cbox = new THREE.Box3();
  let has = false;
  for (const p of parts) if (opts.colliderGroups.includes(p.name)) { p.geometry.computeBoundingBox(); cbox.union(p.geometry.boundingBox!); has = true; }
  if (has) {
    const s = new THREE.Vector3(), c = new THREE.Vector3();
    cbox.getSize(s); cbox.getCenter(c);
    collider = { size: [s.x, s.y, s.z], center: [c.x, c.y, c.z] };
  }
  // Drop points = center of any transparent part (ore spawns there; no collision).
  const dropPoints: [number, number, number][] = [];
  for (const p of parts) if (p.opacities.some((o) => o < 1)) {
    p.geometry.computeBoundingBox();
    const c = new THREE.Vector3(); p.geometry.boundingBox!.getCenter(c);
    dropPoints.push([c.x, c.y, c.z]);
  }
  return { parts, collider, dropPoints };
}

/** Conveyor: `Union1` = 100×20×100 deck (collider); `Union2` = decorative flare. yaw = 90° (asset authored a
 *  quarter-turn off from +Z-forward — see [[longfight-uma3-models-sync]]). */
export const CONVEYOR: FactoryModel = buildModel(conveyorObj, conveyorMtl, { colliderGroups: ["Union1"], yaw: Math.PI / 2 });

/** Dropper (the ORE SOURCE = drill). Anchored on its base TILE (`Union2`), it OVERHANGS the neighbor tile with a
 *  drop-arm ending in a transparent glass cube (the ore spawn point, ~y70 over the fed tile's center). yaw = −90°
 *  aims the overhang along the engine's +Z front, so it hangs over the tile the drill drops onto. */
export const DROPPER_SINGLE: FactoryModel = buildModel(dropperSingleObj, dropperSingleMtl, { colliderGroups: ["Union2"], originGroup: "Union2", yaw: -Math.PI / 2 });

/** Dual dropper: same as single + a second drop point (Part2). Tile = `Union3`. Will drive a 2×-rate "drill2"
 *  building that ALTERNATES the two drop points (clog-avoidance under the physics ore system). */
export const DROPPER_DOUBLE: FactoryModel = buildModel(dropperDoubleObj, dropperDoubleMtl, { colliderGroups: ["Union3"], originGroup: "Union3", yaw: -Math.PI / 2 });

/** Recover the mis-sized conveyor ramp: it was modeled for a ~2.5-tile gap, but it must be a 3-TILE RUN (300)
 *  with a 1-FLOOR RISE (100). Keep the (circular) rollers rigid — bottom stays put, top slides to the 3×1
 *  endpoint — and stretch only the flat belt (`Part1`) + side-rails (`Part3`,`Part8`) between them. Roller groups:
 *  bottom = Part6/4/9, top = Part5/2/7. (One-off surgery for this asset; drop it if the source is re-exported.) */
function recoverRamp(parts: ModelPart[]): void {
  const by: Record<string, ModelPart> = {};
  for (const p of parts) by[p.name] = p;
  const ctr = (p: ModelPart): THREE.Vector3 => { p.geometry.computeBoundingBox(); return p.geometry.boundingBox!.getCenter(new THREE.Vector3()); };
  const B = ctr(by.Part6), T = ctr(by.Part5); // bottom / top roller centers
  const run0 = T.x - B.x, rise0 = T.y - B.y, run1 = 300, rise1 = 100;
  const dx = (B.x + run1) - T.x, dy = (B.y + rise1) - T.y;
  for (const n of ["Part5", "Part2", "Part7"]) by[n].geometry.translate(dx, dy, 0); // slide the top roller group rigidly
  for (const n of ["Part1", "Part3", "Part8"]) {                                     // stretch belt + rails onto the new incline
    const pos = by[n].geometry.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const s = (pos.getX(i) - B.x) / run0;              // fraction along the incline (0 bottom, 1 top)
      const yOff = pos.getY(i) - (B.y + s * rise0);      // vertical offset from the old incline line
      pos.setX(i, B.x + s * run1);
      pos.setY(i, B.y + s * rise1 + yOff);
    }
    pos.needsUpdate = true;
    by[n].geometry.computeVertexNormals();
  }
}

/** Conveyor RAMP (recovered): a 3-tile-long × 1-tile-wide inclined belt that lifts ore one floor (100). Collider
 *  is the sloped belt `Part1` (physics ore will ride UP the slope). Orientation is handled at placement. */
export const RAMP: FactoryModel = buildModel(rampObj, rampMtl, { colliderGroups: ["Part1"], recover: recoverRamp, originGroup: "Part6", yaw: -Math.PI / 2, shift: [0, 0, -50] });

/** Splitter: a 100×100×90 brown housing (`Union3`) OPEN on all 4 sides at ore height + a solid black base deck
 *  (`Union2`) + a low gray lip (`Union1`, marks the intake side) + the no-collision BLACK CENTER box (`Part1`) the
 *  ore rides into before the splitter flings it out one of the 3 non-intake sides. Collider = everything except the
 *  center box (ore must be able to enter it). yaw = +90° puts the intake/lip opposite the dropper's overhang; the
 *  4-way-open passages mean orientation is cosmetic-only — confirm the lip side visually. See [[longfight-uma3-models-sync]]. */
export const SPLITTER: FactoryModel = buildModel(splitterObj, splitterMtl, { colliderGroups: ["Union1", "Union2", "Union3"], yaw: Math.PI / 2 });

/** Conveyor Wall: a thin barrier (Union1 = gold frame 100×70×10, Union2 = ~50% translucent gray panel) that spans a
 *  tile's width and HUGS one edge. Authored spanning X / thin in Z (normal = local +Z), so `shift:[0,0,45]` pushes it
 *  onto the +Z edge; `makeMesh`'s DIR_YAW[dir] then carries it to the placed dir's edge (dir = which edge it walls). */
export const WALL: FactoryModel = buildModel(wallObj, wallMtl, { colliderGroups: ["Union1", "Union2"], shift: [0, 0, 45] });

/** Blaster (MMF ranged fire-beam imbuer): `Union2` = gray body, `Union1` = OUTER red shell (brightens as it charges),
 *  `Part1` = INNER red barrel (long in Z — grows forward toward the ore, then blasts). Both red parts render NEON via
 *  high emissive (the battle has no bloom pass). yaw = π aims the barrel muzzle to +Z (the placed dir) — confirm visually. */
export const BLASTER: FactoryModel = buildModel(blasterObj, blasterMtl, { colliderGroups: ["Union1", "Union2"], yaw: Math.PI });

/** Cryo / Cooler (MMF overhead cooler): the whole model sits HIGH (all mass at y≈70–100) so it's an OVERHEAD unit —
 *  ore passes freely under it. `Union1` = gray housing lid, `Union3` = gray top flywheel (free-spins), `Union2` = the
 *  blue cooling ring (rendered NEON + spun per cooling pulse). No collider (`colliderGroups: []`) — it's a non-blocking
 *  modifier that mounts on top of another building, not a physics obstacle; and no transparent parts ⇒ no drop points.
 *  yaw = +90° matches this author's other belts; facing is cosmetic on a round cooler. See [[longfight-uma3-models-sync]]. */
export const FREEZER: FactoryModel = buildModel(freezerObj, freezerMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Pressurizer (MMF Oven batch trap): `Union1` = thin RED neon strip (static glow), `Union2`/`Union3`/`Union4` =
 *  gray housing / dark floor / dark shell, and the two door PANELS `Part1` (smaller = ENTRANCE) + `Part2` (ENTRANCE's
 *  opposite = EXIT). Authored with ore flowing along +X (entrance −X → exit +X); yaw = −90° turns the exit to the
 *  engine's +Z front so ore enters the back and leaves the front. No collider (physics is handled specially by kind);
 *  door opacity (0.7) is applied in code, NOT the mtl, so the loader doesn't mistake the doors for ore drop points. */
export const PRESSURIZER: FactoryModel = buildModel(pressurizerObj, pressurizerMtl, { colliderGroups: [], yaw: -Math.PI / 2 });

/** Magnetron (MMF Sparkler, magnetic-desk-toy theme): just the FLOATING DISK (`Union1` gray ring + `Union2` brown
 *  inner), authored hovering at y≈60–80. No collider (it floats; the bundled conveyor carries the ore). Re-origin
 *  drops it to local y0–20 — `makeMesh` lifts it back up to `magnetronHoverY` and spins/bobs it. Radially symmetric,
 *  so yaw is cosmetic (0). */
export const MAGNETRON: FactoryModel = buildModel(magnetronObj, magnetronMtl, { colliderGroups: [] });

/** Infernace (MMF Geothermal Extractor, an "infernal furnace"): a self-contained inline fire imbuer with its OWN
 *  conveyor + a clear central channel at ore height — ore rides THROUGH it. `Union1`/`Union3` = gray deck, `Union2` =
 *  dark-red furnace shell, `Union4` = the 4-oval top that stamps DOWN on each ore pass. Ore flows TOWARD Union4;
 *  yaw = −90° turns that flow axis to the engine's +Z front. No collider (physics handled by kind). No glow. */
export const INFERNACE: FactoryModel = buildModel(infernaceObj, infernaceMtl, { colliderGroups: [], yaw: -Math.PI / 2 });

/** Leech (MMF Sapper): a tall tower with its OWN conveyor belt (`Union2` = the flat deck, authored partway up at
 *  y≈100-120). Re-origining ON `Union2` (`originGroup`) pins that belt to the standard conveyor height (y0-20) so the
 *  ore rides at the normal level — which leaves the 4 yellow neon "stake" columns (`Part1..4`) + the base hanging
 *  DOWN below y0 toward the upgrader on the floor below, and the head rising above. No collider (belt handled by kind). */
export const LEECH: FactoryModel = buildModel(leechObj, leechMtl, { colliderGroups: [], originGroup: "Union2", yaw: Math.PI / 2 });

/** Ecobox (MMF Infestor): `Union1` = the belt deck (ore rides through), `Union2` = base, `Union3` = the bright-green
 *  NEON (Kd→sRGB ≈ 91,154,76; glows + pulses per ore pass), `Union4` = dark-green housing. yaw = 90° — same
 *  quarter-turn correction the conveyor/leech needed (asset authored off from +Z-forward). No collider (ride by kind). */
export const ECOBOX: FactoryModel = buildModel(ecoboxObj, ecoboxMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Scanner (MH Premium/Ore Tracker): a magenta readout machine with its own belt (`Union5` = the 100×20×100 deck,
 *  `Union4` = support). `Union1` = the bright-magenta NEON RING — pure blocks in a circle, CENTERED on the belt, the
 *  only part that SPINS (+ snap-kicks 45° + lights up per pass; ore passes THROUGH it, MRI-style). `Union2` = a
 *  bright-magenta neon arc that ties into the ring (glows, STATIC). `Union3` = the big 2-tile dark-magenta housing
 *  (STATIC). Re-origin ON the deck (`Union5`, authored raised at y20-40) to pin the belt to the standard y0-20
 *  height — which drops `Union1` to straddle the ore path (center lands on the belt axis). yaw = 90° like the other belts. */
export const SCANNER: FactoryModel = buildModel(scannerObj, scannerMtl, { colliderGroups: [], originGroup: "Union5", yaw: Math.PI / 2 });

/** Waverider (MH Schrödinger): a 2-TILE-LONG inline upgrader — the belt runs through BOTH tiles. Deck segments
 *  `Union2` (one tile) + `Union4` (the next); `Union3`/`Union5` = under-supports; `Union1` = the dark-blue housing
 *  spanning both; `Union6` = the bright-blue NEON strip (Kd→sRGB ≈ 33,84,185) that WAVES along its length as ore
 *  passes. Re-origin ON `Union2` so the model anchors on that tile and extends one tile along the belt into its
 *  neighbor (the far tile is reserved at placement). yaw = 90° like the other belts. */
export const WAVERIDER: FactoryModel = buildModel(schrodingerObj, schrodingerMtl, { colliderGroups: [], originGroup: "Union2", yaw: -Math.PI / 2 });

/** Triplexer (MMF Caffeinator, renamed): the first L-SHAPED building — a 3-tile belt that TURNS inside itself.
 *  Decks, in authored coords: `Union10`/`Union11` = the entry tile, `Union6`/`Union7` = the corner tile one step
 *  along +X, `Union8`/`Union9` = the exit tile one step further along +Z. So ore enters traveling +X and leaves
 *  traveling +Z — a 90° turn — which is why this needs `yaw: -PI/2` (rotates the ENTRY run onto the engine's
 *  +Z-forward) and why its FOOTPRINT is [[1,0],[1,1]] rather than a straight [[1,0],[2,0]].
 *  Anchored on `Union11`, the entry deck, so the tile the player clicks is where ore goes IN.
 *  `Union1`+`Union4` (exit end) and `Union2`+`Union3` (entry end) are the four thin neon strips. */
/** Teleporter A (entrance) / B (exit): a matched pair, 1 tile each, identical geometry — `Union1` the deck,
 *  `Union3` the cyan pad ore ride over, `Union2`/`Union4` the housing. Only the housing color differs (A dark
 *  teal, B bright green-teal) so the two read apart at a glance across a big base. Authored origin-centered on
 *  their own tile, so no originGroup is needed; yaw matches every other inline belt piece. */
/** Enricher (flat-value adder): plain 1-tile inline belt piece — `Union1` the belt, `Union2` the support,
 *  `Union3` a raised fin over the lane and `Union4` the reddish housing. No neon, no per-building state. */
/** Receptacle (the ore SINK, rebranded from Receptacle). TWO tiles: the anchor carries the belt ore ride in on
 *  (`Union3` 100x20x100, `Union2` the support), and the neighboring tile carries the body and `Part3` — a
 *  100x100x100 block sitting at y -100..0, i.e. BELOW the deck. That tile is meant to hang off the edge of the
 *  build plate so the tube drops into open air, which is why this kind is in FOOTPRINT_OUTBOARD.
 *  `Part1` (80x32x20, high on the outboard end) is the ore-count SCREEN; `Part2` is the red indicator strip.
 *  Source folder is spelled "recepticle" in the Uma3 mirror; the repo copy uses the correct spelling. */
// yaw 0, NOT the PI/2 every other inline piece uses: measured, PI/2 put the tube (Part3) and screen on -perp
// while FOOTPRINT reserves `forward`. Verified in all four rotations that yaw 0 lands belt=anchor, tube=forward.
/** Ore Obliterator (MH "ore clearer", renamed): a 1-tile panic button. `Union1` is the dark body; `Union2` is
 *  the ring of floating neon SPIKES — 142 across, so they overhang the tile by ~21 a side, and they spin and
 *  pulse like a red star. The spike color is driven per-INSTANCE at runtime (it flashes a different hue for
 *  each ability), which is what the vec3 emissive attribute on Pool is for. */
export const OBLITERATOR: FactoryModel = buildModel(obliteratorObj, obliteratorMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** MH Ore Cannon — the traversal answer: it throws ore across a gap or up to another floor. Bundled conveyor deck
 *  (Union1 = gray support 100×15×102, Union2 = dark belt 100×20×100 — the SAME two shapes the real Conveyor uses,
 *  which is how they're identified; color lies, shape doesn't) under a turret: Union3 is the static housing,
 *  Union5 the GEAR that swivels (yaw), Union4 the BARREL that elevates (pitch only — deliberately not a ball
 *  joint). The barrel's trunnion is 20 below its own center, i.e. local y = CANNON_PIVOT_Y, which lands 5 above
 *  the gear's top face. Both moving parts are driven by per-INSTANCE matrices, so a pad full of cannons is still
 *  one draw call per part. */
export const ORECANNON: FactoryModel = buildModel(orecannonObj, orecannonMtl, { colliderGroups: [], yaw: Math.PI / 2 });

export const RECEPTACLE: FactoryModel = buildModel(receptacleObj, receptacleMtl, { colliderGroups: [], originGroup: "Union3", yaw: 0 });

export const ENRICHER: FactoryModel = buildModel(enricherObj, enricherMtl, { colliderGroups: [], yaw: Math.PI / 2 });

export const TELEPORTER_A: FactoryModel = buildModel(teleporteraObj, teleporteraMtl, { colliderGroups: [], yaw: Math.PI / 2 });
export const TELEPORTER_B: FactoryModel = buildModel(teleporterbObj, teleporterbMtl, { colliderGroups: [], yaw: Math.PI / 2 });

export const TRIPLEXER: FactoryModel = buildModel(triplexerObj, triplexerMtl, { colliderGroups: [], originGroup: "Union11", yaw: -Math.PI / 2 });

/** Diversifier (MMF): a 1-tile inline upgrader that pays off ELEMENTAL VARIETY. `Union2` = the standard belt deck
 *  (100×15×102, the same part every inline building has), `Union1`/`Union3` = housing + under-support. The three
 *  70-tall slabs straddling the lane are the whole idea: `Union4` RED, `Union5` GREEN, `Union6` BLUE, sitting
 *  side by side across the tile, each with a matching half-transparent window (`Part1`/`Part2`/`Part3`). They are
 *  the neon — an ore carrying more elements lights more of them. No colliders: ore ride the belt straight
 *  through, like every other upgrader. yaw = 90° like the other belts. */
export const DIVERSIFIER: FactoryModel = buildModel(diversifierObj, diversifierMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Mutator (MMF): 1-tile inline chaos upgrader. `Union4` = the standard 100×20×100 deck, `Union2` = the 102-long
 *  gray frame, `Union1` + `Union3` = the burnt-orange housing (Kd→sRGB ≈ 214,116,58) that reads as a reactor, and
 *  `Part1` a small nub on one corner. No colliders — ore ride the belt straight through. yaw = 90° like the belts. */
export const MUTATOR: FactoryModel = buildModel(mutatorObj, mutatorMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Laser (MMF): 1-tile inline precision upgrader. `Union3` = the 100×20×100 deck, `Union1` = the 102-long gray
 *  frame, `Union2` = the tall pure-RED emitter housing (255,0,0) — the neon, which fires on a hit and stays lit
 *  while the machine holds a charge. No colliders. yaw = 90° like the belts. */
export const LASER: FactoryModel = buildModel(laserObj, laserMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Bomb Packer (MMF): 1-tile inline. `Union4` = the 100×20×100 deck, `Union3` = the 102-long gray frame,
 *  `Union2` = the dark navy casing (20,37,50), `Union1` = a thin RED strip across the top — the neon, which
 *  reads as the arming light. No colliders. yaw = 90° like the belts. */
export const PACKER: FactoryModel = buildModel(packerObj, packerMtl, { colliderGroups: [], yaw: Math.PI / 2 });

/** Vat (MMF): a SECOND furnace/sink, and the only one of these with no belt deck — `Union1` is a 100×120×100
 *  dark-red tank that ore drop into. `Union2` is a spout arm that OVERHANGS the tile by ~61 along +X, tipped
 *  with the transparent `Part1` cube the loader reads as a DROP POINT: that is where the Amalgam comes out.
 *  `Part2` is the red liquid surface. Re-origin on `Union1` so the tank sits on its tile and the spout hangs
 *  off it, the same treatment the dropper's arm gets. */
export const VAT: FactoryModel = buildModel(vatObj, vatMtl, { colliderGroups: [], originGroup: "Union1", yaw: Math.PI / 2 });
